import * as React from "react";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Modal } from "../../../ui/components/Modal";
import { Callout } from "../../../ui/components/Callout";
import { TextInput } from "../../../ui/components/Form";
import { MediaUploader } from "../../../ui/components/MediaUploader";
import type { MediaEntry } from "../../../ui/components/MediaUploader";
import { useConfirmModal } from "../../hooks/useConfirmModal";
import { useI18n } from "../../lib/i18n";
import {
  createCertificationModule,
  updateCertificationModule,
  requestHeroImageUpload,
} from "../../lib/api/certifications";
import type { CertificationModule } from "../../lib/api/certifications";

/** Detects the presigned-S3 vs local-proxy PUT convention used across the app's upload flows. */
async function uploadModuleHeroImageFile(
  accessToken: string,
  file: File,
  failMessage: string,
): Promise<string> {
  const instructions = await requestHeroImageUpload(accessToken, file.type);
  const isPresigned = instructions.uploadUrl.includes("X-Amz-Signature");
  const headers: Record<string, string> = { "Content-Type": file.type };
  if (!isPresigned) {
    if (instructions.key) headers["X-Avatar-Key"] = instructions.key;
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  }
  const res = await fetch(instructions.uploadUrl, { method: "PUT", body: file, headers });
  if (!res.ok) {
    throw new Error(failMessage);
  }
  return instructions.avatarUrl;
}

export interface ModuleFormModalProps {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  /** When provided, editing an existing module. Otherwise, creating a new one. */
  existing?: CertificationModule;
  onSaved: (module: CertificationModule) => void;
}

interface FormValues {
  title: string;
  slug: string;
  description: string;
  heroImageUrl: string;
  isActive: boolean;
}

const emptyForm = (): FormValues => ({
  title: "",
  slug: "",
  description: "",
  heroImageUrl: "",
  isActive: false,
});

function moduleToForm(m: CertificationModule): FormValues {
  return {
    title: m.title,
    slug: m.slug,
    description: m.description ?? "",
    heroImageUrl: m.heroImageUrl ?? "",
    isActive: m.isActive,
  };
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

export function ModuleFormModal({
  open,
  onClose,
  accessToken,
  existing,
  onSaved,
}: ModuleFormModalProps) {
  const { t } = useI18n();
  const isEdit = !!existing;
  const { confirm, ConfirmModal } = useConfirmModal();

  const [values, setValues] = React.useState<FormValues>(
    existing ? moduleToForm(existing) : emptyForm(),
  );
  const [slugTouched, setSlugTouched] = React.useState(isEdit);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setValues(existing ? moduleToForm(existing) : emptyForm());
    setSlugTouched(!!existing);
    setError(null);
  }, [open, existing]);

  const set = (field: keyof FormValues, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleTitleChange = (title: string) => {
    setValues((prev) => ({
      ...prev,
      title,
      slug: slugTouched ? prev.slug : slugify(title),
    }));
    setError(null);
  };

  const handleActiveChange = async (nextActive: boolean) => {
    if (nextActive === values.isActive) return;
    const confirmed = await confirm(
      t(
        nextActive
          ? "pro.system.modules.confirm.activateTitle"
          : "pro.system.modules.confirm.deactivateTitle",
      ),
      t(
        nextActive
          ? "pro.system.modules.confirm.activateBody"
          : "pro.system.modules.confirm.deactivateBody",
      ),
      t(
        nextActive
          ? "pro.system.modules.confirm.activate"
          : "pro.system.modules.confirm.deactivate",
      ),
      nextActive ? "primary" : "danger",
    );
    if (confirmed) set("isActive", nextActive);
  };

  const heroImageItems: MediaEntry[] = React.useMemo(
    () => (values.heroImageUrl ? [{ id: existing?.id ?? "hero-image", url: values.heroImageUrl }] : []),
    [values.heroImageUrl, existing?.id],
  );

  // Editing an existing module: persist the hero image immediately, matching the
  // standardized immediate-persistence behavior used everywhere else MediaUploader
  // is wired up (avatar, org logo). Creating a new module: there's no module id yet
  // to persist against, so just stage it in local form state until the form's own
  // Save/Add button is pressed — the same unavoidable exception CreateOrganizationModal
  // has for its logo.
  const handleHeroImageApply = async (next: MediaEntry[]) => {
    const url = next[0]?.url ?? "";
    set("heroImageUrl", url);
    if (existing) {
      const updated = await updateCertificationModule(accessToken, existing.id, { heroImageUrl: url || null });
      onSaved(updated);
    }
  };

  const handleHeroImageRemove = async () => {
    set("heroImageUrl", "");
    if (existing) {
      const updated = await updateCertificationModule(accessToken, existing.id, { heroImageUrl: null });
      onSaved(updated);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!values.title.trim()) {
      setError(t("pro.system.modules.validation.titleRequired"));
      return;
    }
    if (!values.slug.trim()) {
      setError(t("pro.system.modules.validation.slugRequired"));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        title: values.title.trim(),
        slug: values.slug.trim(),
        description: values.description.trim() || null,
        heroImageUrl: values.heroImageUrl.trim() || null,
        isActive: values.isActive,
      };

      const saved = isEdit
        ? await updateCertificationModule(accessToken, existing!.id, payload)
        : await createCertificationModule(accessToken, payload);

      onSaved(saved);
      onClose();
    } catch (err: any) {
      setError(err?.message ?? t("pro.system.modules.error.save"));
    } finally {
      setSaving(false);
    }
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 500,
    color: "#6B7280",
    display: "block",
    marginBottom: 4,
  };

  return (
    <Modal open={open} setOpen={(v) => { if (!v) onClose(); }} minWidth="440px" maxWidth="520px" closeOnClickOff>
      <Modal.Form onSubmit={handleSubmit}>
        <Modal.Header>
          <Text level="h3" weight="bold">
            {isEdit ? t("pro.system.modules.form.editTitle") : t("pro.system.modules.form.createTitle")}
          </Text>
        </Modal.Header>
        <Modal.Body>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {error && <Callout variant="error">{error}</Callout>}

          <TextInput
            label={t("pro.system.modules.form.titleRequired")}
            value={values.title}
            onChange={(e) => handleTitleChange(e.target.value)}
            disabled={saving}
            placeholder={t("pro.system.modules.form.titlePlaceholder")}
            autoFocus
          />

          <TextInput
            label={t("pro.system.modules.form.slugRequired")}
            value={values.slug}
            onChange={(e) => { setSlugTouched(true); set("slug", slugify(e.target.value)); }}
            disabled={saving}
            placeholder={t("pro.system.modules.form.slugPlaceholder")}
          />

          <div>
            <label style={labelStyle}>{t("pro.system.modules.form.description")}</label>
            <textarea
              value={values.description}
              onChange={(e) => set("description", e.target.value)}
              disabled={saving}
              rows={3}
              placeholder={t("pro.system.modules.form.descriptionPlaceholder")}
              style={{
                padding: "8px 10px",
                borderRadius: 6,
                border: "1px solid #D1D5DB",
                fontSize: 14,
                background: "var(--color-inputBg, #fff)",
                color: "var(--color-fg, #111)",
                width: "100%",
                boxSizing: "border-box",
                resize: "vertical",
              }}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("pro.system.modules.form.heroImage")}</label>
            <MediaUploader
              mediaType="image"
              items={heroImageItems}
              onChange={() => {}}
              onApply={handleHeroImageApply}
              onRemove={handleHeroImageRemove}
              uploadFile={(file) =>
                uploadModuleHeroImageFile(
                  accessToken,
                  file,
                  t("pro.system.modules.error.uploadHero"),
                )
              }
              maxItems={1}
              shape="rectangle"
              placeholder={t("pro.system.modules.form.heroPlaceholder")}
              error={error}
              onError={setError}
            />
          </div>

          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 14 }}>
            <input
              type="checkbox"
              checked={values.isActive}
              onChange={(e) => void handleActiveChange(e.target.checked)}
              disabled={saving}
            />
            {t("pro.system.modules.form.active")}
          </label>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" color="primary" variant="outline" onClick={onClose} disabled={saving}>
            {t("shared.common.cancel")}
          </Button>
          <Button type="submit" color="primary" variant="solid" loading={saving}>
            {isEdit ? t("pro.system.modules.form.saveChanges") : t("pro.system.modules.form.addModule")}
          </Button>
        </Modal.Footer>
      </Modal.Form>
      <ConfirmModal />
    </Modal>
  );
}
